import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Circle,
  useMap,
} from "react-leaflet";
import * as L from "leaflet";
import { LatLngExpression } from "leaflet";
import { useEffect, useRef } from "react";
import { VehicleLocation } from "../types";
import { LiveTrackingLocation } from "../hooks/useLiveTrackingSocket";
import { UserLocation } from "../hooks/useUserLocation";

// Default marker icon (ensures compatibility in various bundlers)
// Casting L as any to avoid transient type issues with Leaflet typings
const vehicleIcon = (L as any).icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

type AnyLocation = VehicleLocation | LiveTrackingLocation;

interface MapViewProps {
  locations: AnyLocation[];
  latest?: AnyLocation;
  onInitialCenter?: (center: [number, number]) => void;
  userLocation?: UserLocation | null;
}

function MapEffects({
  latest,
  onInitialCenter,
}: {
  latest?: AnyLocation;
  onInitialCenter?: (c: [number, number]) => void;
}) {
  const map = useMap();
  const firstCentered = useRef(false);

  useEffect(() => {
    if (!firstCentered.current && latest) {
      console.log("MapEffects: Centering map to latest location", latest);
      const center: [number, number] = [latest.latitude, latest.longitude];
      map.setView(center, 15);
      firstCentered.current = true;
      onInitialCenter?.(center);
    }
  }, [latest, map, onInitialCenter]);

  useEffect(() => {
    if (latest && firstCentered.current) {
      const latlng = L.latLng(latest.latitude, latest.longitude);
      if (!map.getBounds().contains(latlng)) {
        map.panTo(latlng);
      }
    }
  }, [latest, map]);

  // Invalidate size on resize / orientation change for responsive layout
  useEffect(() => {
    const invalidate = () => {
      setTimeout(() => map.invalidateSize(), 150);
    };
    window.addEventListener("resize", invalidate);
    screen.orientation?.addEventListener?.("change", invalidate as any);
    return () => {
      window.removeEventListener("resize", invalidate);
      screen.orientation?.removeEventListener?.("change", invalidate as any);
    };
  }, [map]);

  return null;
}

export function MapView({
  locations,
  latest,
  onInitialCenter,
  userLocation,
}: MapViewProps) {
  const path: [number, number][] = locations.map((l) => [
    l.latitude,
    l.longitude,
  ]);
  // Cast MapContainer temporarily to any due to React 19 + react-leaflet type lag
  const AnyMap: any = MapContainer;

  return (
    <AnyMap
      center={[0, 0] as LatLngExpression}
      zoom={2}
      style={{ height: "100%", width: "100%" }}
    >
      <MapEffects latest={latest} onInitialCenter={onInitialCenter} />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {latest && (
        <>
          {/* @ts-ignore Temporary suppression: react-leaflet v5 types not aligned with React 19 for Marker icon prop */}
          <Marker
            {...({
              position: [latest.latitude, latest.longitude] as LatLngExpression,
              icon: vehicleIcon,
            } as any)}
          />
          {latest.accuracy && latest.accuracy > 0 && (
            <Circle
              center={[latest.latitude, latest.longitude] as LatLngExpression}
              radius={latest.accuracy}
              pathOptions={{ color: "#3388ff", opacity: 0.3, fillOpacity: 0.1 }}
            />
          )}
        </>
      )}
      {path.length > 1 && (
        <Polyline positions={path as any} pathOptions={{ color: "red" }} />
      )}
      {userLocation && (
        <>
          {/* <Circle
            center={
              [
                userLocation.latitude,
                userLocation.longitude,
              ] as LatLngExpression
            }
            radius={userLocation.accuracy || 0.5}
            pathOptions={{
              color: "#00bfa5",
              weight: 2,
              opacity: 0.8,
              fillOpacity: 0.15,
            }}
          /> */}
          {/* User dot using DivIcon */}
          {/* @ts-ignore */}
          <Marker
            {...({
              position: [
                userLocation.latitude,
                userLocation.longitude,
              ] as LatLngExpression,
              icon: (L as any).divIcon({
                className: "user-location-dot",
                html: '<div class="ul-dot"></div>',
                iconSize: [16, 16],
                iconAnchor: [8, 8],
              }),
            } as any)}
          />
        </>
      )}
    </AnyMap>
  );
}
